import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  FormControlLabel,
} from "@mui/material";

/**
 * Format a permission string into a readable label
 * @param {string} permission - The permission string (e.g., "show_dashboard")
 * @returns {string} - Formatted permission label (e.g., "Show")
 */
const formatPermissionLabel = permission => {
  if (!permission) return "";

  // Extract the action part of the permission (e.g., "show" from "show_dashboard")
  const action = permission.split("_")[0];

  // Capitalize the first letter
  return action.charAt(0).toUpperCase() + action.slice(1);
};

/**
 * Format a module name from a permission
 * @param {string} permission - The permission string (e.g., "show_dashboard")
 * @returns {string} - Formatted module name (e.g., "Dashboard")
 */
const formatModuleName = permission => {
  if (!permission) return "";

  // Extract the resource part of the permission (e.g., "dashboard" from "show_dashboard")
  const parts = permission.split("_");
  if (parts.length < 2) return "";

  // Get the resource part and capitalize the first letter
  const resource = parts.slice(1).join(" ");
  return resource.charAt(0).toUpperCase() + resource.slice(1);
};

/**
 * PermissionTable - A reusable component for displaying and selecting permissions
 * @param {Object} props - Component props
 * @param {Array} props.permissionGroups - Array of permission groups with their associated permissions
 * @param {Object} props.selectedPermissions - Object with permission names as keys and boolean values
 * @param {Function} props.onPermissionChange - Callback when permission selection changes
 * @param {Function} props.onGroupChange - Callback when a group selection changes
 */
const PermissionTable = ({
  permissionGroups,
  selectedPermissions,
  onPermissionChange,
  onGroupChange,
}) => {
  // Check if all permissions in a group are selected
  const isGroupSelected = permissions => {
    return permissions.every(permission => selectedPermissions[permission]);
  };

  // Check if some but not all permissions in a group are selected
  const isGroupIndeterminate = permissions => {
    const selected = permissions.filter(
      permission => selectedPermissions[permission],
    );
    return selected.length > 0 && selected.length < permissions.length;
  };

  // Handle toggling an entire group
  const handleGroupToggle = (permissions, isSelected) => {
    const updates = {};
    permissions.forEach(permission => {
      updates[permission] = !isSelected;
    });
    onGroupChange(updates);
  };

  return (
    <TableContainer
      component={Paper}
      className="mt-4 border border-[#E5E7EB] rounded-lg">
      <Table aria-label="permissions table">
        <TableHead>
          <TableRow>
            <TableCell
              className="bg-[#F9FAFB] font-outfit font-medium text-base text-[#374151] border-b border-[#E5E7EB]"
              width="25%">
              Module
            </TableCell>
            <TableCell className="bg-[#F9FAFB] font-outfit font-medium text-base text-[#374151] border-b border-[#E5E7EB]">
              Permissions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {permissionGroups.map(group => {
            const isSelected = isGroupSelected(group.permissions);
            const isIndeterminate = isGroupIndeterminate(group.permissions);

            return (
              <TableRow key={group.module} hover>
                <TableCell className="border-b border-[#E5E7EB] font-outfit text-base text-[#374151]">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isSelected}
                        indeterminate={isIndeterminate}
                        onChange={() =>
                          handleGroupToggle(group.permissions, isSelected)
                        }
                        sx={{
                          color: "#83899F",
                          "&.Mui-checked": {
                            color: "#5652B0",
                          },
                          "&.MuiCheckbox-indeterminate": {
                            color: "#5652B0",
                          },
                        }}
                      />
                    }
                    label={group.module}
                    className="font-outfit"
                  />
                </TableCell>
                <TableCell className="border-b border-[#E5E7EB]">
                  <div className="grid grid-cols-5 gap-x-4 gap-y-3">
                    {group.permissions.map(permission => {
                      // Extract action name from permission string (e.g., "show" from "show_dashboard")
                      const permissionLabel = formatPermissionLabel(permission);

                      return (
                        <div key={permission} className="flex items-center">
                          <Checkbox
                            checked={selectedPermissions[permission] || false}
                            onChange={() => onPermissionChange(permission)}
                            sx={{
                              color: "#83899F",
                              "&.Mui-checked": {
                                color: "#5652B0",
                              },
                            }}
                          />
                          <span className="font-outfit text-sm text-[#4B5563]">
                            {permissionLabel}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PermissionTable;
